import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldProps } from "react-jsonschema-form";
import InputGroup from "../../../witadmin/CRUD/Common/InputGroup";
import { FormattedMessage, useIntl } from "react-intl";
import { DailyValues, SelectedService } from "../../../modification/model";
import DatePickerGroup from "../../../witadmin/CRUD/Common/DatePickerGroup";
import { addDays, format, isBefore, subDays, isWithinInterval, isAfter } from "date-fns";
import TextAreaGroup from "../../../witadmin/CRUD/Common/TextAreaGroup";
import { cloneDeep } from "lodash";
import { useConfig } from "../../../../lib/config";
import { useHttpClient } from "../../../../lib/http";
import { useProperty } from "../../../../components/property-provider";
import { RATE_TYPE } from "../../../witadmin/Common/constants";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePropertyConfiguration } from "../../../../components/property-configuration-provider";
import GridRow from "./gridRow";
import { AriValues, FinalPrice, Row } from "./types";
import {
  BookingDetails,
  CloseIcon,
  DatePickerGroupWrapper,
  Extras,
  ExtrasTotal,
  GrandTotalWrapper,
  Grid,
  GridAppliedPrice,
  GridAriPrice,
  GridDate,
  GridDiscount,
  GridFinalPrice,
  GridPromos,
  GridRestrictions,
  GridSeparator,
  HelpTextMessage,
  HelpTextMessageNoMargin,
  NetPriceTotalWrapper,
  PriceLine,
  StyledFontAwesomeIconWrapper,
  StyledModificationPriceManagement,
  StyledReactTooltip,
  StyledSelector,
} from "./styles";
import { messages } from "./messages";
import { generateRows } from "./utils";

const regex = RegExp(/^[0-9]\d{0,99}(\.\d{0,2})?%?$/);

export function ModificationPriceManagement({
  formData,
  onChange,
  rawErrors,
  disabled,
  schema,
}: FieldProps) {
  const intl = useIntl();
  const { ticker: propertyTicker, info } = useProperty();
  const config = useConfig();
  const newAriEnabled = !!Number(usePropertyConfiguration()?.showNewAriManagement);
  const apiClient = useHttpClient(config.apiEndpoint);

  const [rows, setRows] = useState<Row[]>([]);
  const [initialAriFetched, setInitialAriFetched] = useState<boolean>(false);
  const [finalPrices, setFinalPrices] = useState<FinalPrice[]>([]);
  const prevCheckIn = useRef<string>("");
  const prevCheckOut = useRef<string>("");
  const prevSelectedInventoryTicker = useRef<string>("");
  const [selectedInventoryTicker, setSelectedInventoryTicker] = useState<string>(
    () => formData?.inventoryTicker || ""
  );

  const extras = useMemo(() => formData?.selectedServices || [], [formData]);
  const quantity = useMemo(() => formData?.quantity || 1, [formData]);
  const comments = useMemo(() => formData?.comments || "", [formData]);
  const internalNotes = useMemo(() => formData?.internalNotes || "", [formData]);
  const guaranteeAmount = useMemo(() => formData?.guaranteeAmount || 0, [formData]);

  const handleUserKeyPress = useCallback(event => {
    const { keyCode } = event;
    if (keyCode === 13) {
      event.preventDefault();
    }
  }, []);

  // Necessary so form is not submitted on enter key press
  useEffect(() => {
    window.addEventListener("keydown", handleUserKeyPress);
    return () => {
      window.removeEventListener("keydown", handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  const calculateTotalValues = useCallback(
    ({
      currentFinalPrices,
      currentExtras,
      currentQuantity,
    }: {
      currentFinalPrices?: FinalPrice[];
      currentExtras?: SelectedService[];
      currentQuantity?: string | number;
    }) => {
      const prices = currentFinalPrices || finalPrices;
      const quant = Number(currentQuantity || quantity);
      const extra = currentExtras || extras;
      const valuesTotal = (
        prices.reduce(
          (accum: number, curr: { finalPrice: number | undefined }) =>
            Number(curr.finalPrice || 0) + accum,
          0
        ) * quant
      ).toFixed(2);

      const extrasTotal = extra
        .reduce((accum: number, curr: { amount: number }) => Number(curr.amount) + accum, 0)
        .toFixed(2);

      const grandTotal = (Number(valuesTotal) + Number(extrasTotal)).toFixed(2);

      return { valuesTotal, extrasTotal, grandTotal };
    },
    [finalPrices, extras, quantity]
  );

  const valuesTotal = useMemo(() => {
    const { valuesTotal } = calculateTotalValues({});
    return valuesTotal;
  }, [calculateTotalValues]);
  const extrasTotal = useMemo(() => {
    const { extrasTotal } = calculateTotalValues({});
    return extrasTotal;
  }, [calculateTotalValues]);
  const grandTotal = useMemo(() => {
    const { grandTotal } = calculateTotalValues({});
    return grandTotal;
  }, [calculateTotalValues]);

  const fetchAri = useCallback<(startDate: string, endDate: string) => Promise<AriValues>>(
    async (startDate: string, endDate: string): Promise<AriValues> => {
      return await apiClient.get<any>(
        `/api/hotel/${propertyTicker}/ari-values?startDate=${startDate}&endDate=${endDate}&onlyInventories=true&inventoryTicker=${selectedInventoryTicker}`
      );
    },
    [propertyTicker, apiClient]
  );

  const calculateFinalPrices = useCallback(
    (rows: Row[], newCheckIn?, newCheckOut?, newSelectedInventoryTicker?) => {
      if (rows) {
        let rowsClone = cloneDeep(rows);
        rowsClone = rowsClone.map(row => {
          row.finalPrice = row.appliedPrice;

          if (row.discount && row.appliedPrice) {
            row.finalPrice = Number((row.appliedPrice - Number(row.discount)).toFixed(2));
          }

          if (typeof row.discount === "string" && row.discount.includes("%") && row.appliedPrice) {
            const discount = row.discount.substring(0, row.discount.length - 1);
            row.finalPrice = Number(
              (row.appliedPrice - (row.appliedPrice / 100) * Number(discount)).toFixed(2)
            );
          }

          return row;
        });
        const finPrices = rowsClone.map(it => ({ date: it.date, finalPrice: it.finalPrice }));
        setFinalPrices(finPrices);

        const dailyValues = finPrices
          .filter(it => it.finalPrice || it.finalPrice === 0)
          .map(it => ({ ticker: it.date, amount: it.finalPrice }));
        const { grandTotal } = calculateTotalValues({ currentFinalPrices: finPrices });
        onChange({
          ...formData,
          inventoryTicker: newSelectedInventoryTicker || formData.inventoryTicker,
          checkIn: newCheckIn || formData.checkIn,
          checkOut: newCheckOut || formData.checkOut,
          dailyValues,
          totalAmount: grandTotal,
        });
      }
    },
    [onChange, formData, calculateTotalValues, selectedInventoryTicker]
  );

  const fetchData = useCallback(
    async function fetchData(params?: {
      newCheckIn?: string;
      newCheckOut?: string;
      newSelectedInventoryTicker?: string;
    }) {
      const checkIn = params?.newCheckIn || formData.checkIn;
      const checkOut = params?.newCheckOut || formData.checkOut;
      const inventoryTicker = params?.newSelectedInventoryTicker || selectedInventoryTicker;
      if (
        ((formData.dailyValues &&
          (prevCheckIn.current !== checkIn || prevCheckOut.current !== checkOut)) ||
          prevSelectedInventoryTicker.current !== inventoryTicker) &&
        isBefore(new Date(checkIn), new Date(checkOut))
      ) {
        const threeDayBefore = subDays(new Date(checkIn), 3);
        const threeDayAfter = addDays(new Date(checkOut), 3);
        const startDate = format(threeDayBefore, "yyyy-MM-dd");
        const endDate = format(threeDayAfter, "yyyy-MM-dd");

        const ariValues = await fetchAri(startDate, endDate);
        const promos = await apiClient.get<any>(
          `/api/property/${propertyTicker}/engine/administration/promo/`
        );

        const sortedPromos = promos
          .filter((promo: any) => promo.isActive)
          .map((promo: any) => {
            let openBookingStartDate = "";
            let openBookingEndDate = "";
            let multiOpenBooking = false;
            if (
              Array.isArray(promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value) &&
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value.length > 1
            ) {
              multiOpenBooking = true;
            }
            if (
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value &&
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value[0]?.startDate
            ) {
              const index = promo.restrictions.OPEN_BOOKING.restrictionValue.value[0].startDate.indexOf(
                "T"
              );
              openBookingStartDate = promo.restrictions.OPEN_BOOKING.restrictionValue.value[0].startDate.substring(
                0,
                index
              );
            }

            if (
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value &&
              promo?.restrictions?.OPEN_BOOKING?.restrictionValue?.value[0]?.endDate
            ) {
              const index = promo.restrictions.OPEN_BOOKING.restrictionValue.value[0].endDate.indexOf(
                "T"
              );
              openBookingEndDate = promo.restrictions.OPEN_BOOKING.restrictionValue.value[0].endDate.substring(
                0,
                index
              );
            }

            return {
              ticker: promo.ticker,
              amount: promo?.rate?.rateValue?.value,
              type: promo.rate.rateType,
              openTravelStartDate: `${
                (promo?.restrictions?.OPEN_TRAVEL?.restrictionValue?.value &&
                  promo?.restrictions?.OPEN_TRAVEL?.restrictionValue?.value[0]?.startDate) ||
                ""
              }`,
              openTravelEndDate: `${
                (promo?.restrictions?.OPEN_TRAVEL?.restrictionValue?.value &&
                  promo?.restrictions?.OPEN_TRAVEL?.restrictionValue?.value[0]?.endDate) ||
                ""
              }`,
              openBookingStartDate,
              openBookingEndDate,
              multiOpenBooking,
              minStay: promo?.restrictions?.MIN_STAY?.restrictionValue?.value?.duration,
              maxStay: promo?.restrictions?.MAX_STAY?.restrictionValue?.value?.duration,
              maxNotice: promo?.restrictions?.MIN_NOTICE?.restrictionValue?.value?.duration,
              minNotice: promo?.restrictions?.MAX_NOTICE?.restrictionValue?.value?.duration,
              discountedUnits: promo?.discountPeriodicity?.discountedUnits,
              applyEveryNumberOfUnits: promo?.discountPeriodicity?.applyEveryNumberOfUnits,
              group: promo?.group,
            };
          });
        const items: Row[] = generateRows({
          ariValues,
          selectedInventoryTicker: inventoryTicker,
          sortedPromos,
          checkIn,
          checkOut,
          initial: true,
        });
        formData.dailyValues.forEach((it: { amount: number; ticker: string }) => {
          const itemIndex = items.findIndex(item => item.date === it.ticker);
          if (
            itemIndex !== -1 &&
            isWithinInterval(new Date(it.ticker), {
              start: new Date(checkIn),
              end: subDays(new Date(checkOut), 1),
            })
          ) {
            items[itemIndex].appliedPrice = it.amount;
            if (formData.originalPromoCodes) {
              items[itemIndex].promos = formData.originalPromoCodes.map((it: any) => ({
                ticker: it.name,
                amount: it.percentage
                  ? Math.abs(it.value)
                  : {
                      amount: Math.abs(it.value),
                      currency: it.totalDiscount?.currency,
                    },
                type: it.percentage ? RATE_TYPE.PERCENTAGE : RATE_TYPE.CONSTANT,
              }));
            }
          }
        });
        prevCheckIn.current = checkIn;
        prevCheckOut.current = checkOut;
        prevSelectedInventoryTicker.current = inventoryTicker;
        setRows(items);
        calculateFinalPrices(items, params?.newCheckIn, params?.newCheckOut,params?.newSelectedInventoryTicker);
      }
    },
    [
      calculateFinalPrices,
      propertyTicker,
      formData.checkIn,
      formData.checkOut,
      formData.dailyValues,
      formData.originalPromoCodes,
      selectedInventoryTicker,
      apiClient,
      fetchAri,
      newAriEnabled,
    ]
  );

  useEffect(() => {
    if (!initialAriFetched) {
      fetchData();
      setInitialAriFetched(true);
    }
  }, [initialAriFetched]);

  useEffect(() => {
    async function fetchGhostDate() {
      let ariValues;
      const realDateEnd = rows && rows.length - 3;
      let currentGhostDate;
      if (rows.length && rows[2].appliedPrice) {
        const dayBeforeStart = subDays(new Date(rows[0].date), 1);
        const starDate = format(dayBeforeStart, "yyyy-MM-dd");
        const twoDaysBeforeStart = subDays(new Date(rows[0].date), newAriEnabled ? 0 : 1);
        const endDate = format(twoDaysBeforeStart, "yyyy-MM-dd");
        ariValues = await fetchAri(starDate, endDate);
        currentGhostDate = dayBeforeStart;
      }
      if (rows.length && realDateEnd && rows[realDateEnd].appliedPrice) {
        const index = rows.length;
        const dayAfterEnd = addDays(new Date(rows[index - 1].date), 1);
        const starDate = format(dayAfterEnd, "yyyy-MM-dd");
        const twoDaysAfterEnd = addDays(new Date(rows[index - 1].date), newAriEnabled ? 2 : 1);
        const endDate = format(twoDaysAfterEnd, "yyyy-MM-dd");
        ariValues = await fetchAri(starDate, endDate);
        currentGhostDate = dayAfterEnd;
      }
      if (ariValues) {
        const promos = await apiClient.get<any>(
          `/api/property/${propertyTicker}/engine/administration/promo/`
        );
        const sortedPromos = promos.map((promo: any) => ({
          ticker: promo.ticker,
          amount: promo?.rate?.rateValue?.value,
          type: promo.rate.rateType,
          group: promo?.group,
        }));
        const items: Row[] = generateRows({
          ariValues,
          selectedInventoryTicker,
          sortedPromos,
          checkIn: formData.checkIn,
          checkOut: formData.checkOut,
          currentGhostDate,
        });

        if (items.length) {
          const ro = [...rows, ...items].sort((a, b) => {
            if (isBefore(new Date(a.date), new Date(b.date))) {
              return -1;
            }
            if (isAfter(new Date(a.date), new Date(b.date))) {
              return 1;
            }
            return 0;
          });
          setRows(ro);
          calculateFinalPrices(ro);
        }
      }
    }
    fetchGhostDate();
  }, [
    calculateFinalPrices,
    rows,
    apiClient,
    propertyTicker,
    selectedInventoryTicker,
    fetchAri,
    newAriEnabled,
    formData.checkIn,
    formData.checkOut,
  ]);

  const handleSelectorChange = useCallback(
    option => {
      const formDta = cloneDeep(formData);

      formDta.inventoryTicker = option.value;

      setSelectedInventoryTicker(option.value);
      onChange(formDta);
      fetchData({ newSelectedInventoryTicker: option.value });
    },
    [onChange, formData]
  );

  const handleChange = useCallback(
    (value, date, type, regex) => {
      const rowsClone = cloneDeep(rows);
      const rowIndex = rowsClone?.findIndex(it => it.date === date);
      value = regex ? (String(value).match(regex) ? value : 0) : value;
      //@ts-ignore
      rowsClone[rowIndex][type] = value;
      setRows(rowsClone);
      calculateFinalPrices(rowsClone);
    },
    [rows, calculateFinalPrices]
  );

  const handleDeletePrice = useCallback(
    (index, type) => {
      const rowsClone = cloneDeep(rows);
      delete rowsClone[index].appliedPrice;
      delete rowsClone[index].discount;
      if (type === "top") {
        rowsClone.shift();
      } else if (type === "bottom") {
        rowsClone.pop();
      }
      setRows(rowsClone);
      calculateFinalPrices(rowsClone);
    },
    [rows, calculateFinalPrices]
  );

  const handleDeleteExtras = useCallback(
    index => {
      const newExtras = formData.selectedServices.map((it: SelectedService) => ({ ...it }));
      newExtras.splice(index, 1);
      const { grandTotal } = calculateTotalValues({ currentExtras: newExtras });
      onChange({ ...formData, selectedServices: newExtras, totalAmount: grandTotal });
    },
    [formData, onChange, calculateTotalValues]
  );

  const handleChangeDeposit = useCallback(
    data => {
      if (regex.test(data.event.target.value)) {
        onChange({
          ...formData,
          guaranteeAmount: data.event.target.value,
        });
      }
    },
    [formData, onChange]
  );

  const handleChangeQuantity = useCallback(
    data => {
      const { grandTotal } = calculateTotalValues({ currentQuantity: data.event.target.value });
      onChange({ ...formData, quantity: Number(data.event.target.value), totalAmount: grandTotal });
    },
    [formData, onChange, calculateTotalValues]
  );

  const handleChangeString = useCallback(
    (type, data) => {
      onChange({ ...formData, [type]: data.event.target.value });
    },
    [formData, onChange]
  );

  const handleChangeDate = useCallback(
    (data, type) => {
      if (type === "checkIn") {
        fetchData({ newCheckIn: data.value });
      } else {
        fetchData({ newCheckOut: data.value });
      }
      onChange({ ...formData, [type]: data.value });
    },
    [formData, onChange]
  );

  const options = useMemo(() => {
    const optns: { value: string; label: string }[] = [];
    //@ts-ignore
    if (Array.isArray(schema?.properties?.inventoryTicker?.enum)) {
      //@ts-ignore
      schema.properties.inventoryTicker.enum.forEach((it, idx) =>
        optns.push({
          value: it as string,
          //@ts-ignore
          label: schema?.properties?.inventoryTicker?.enumNames[idx],
        })
      );
    }
    return optns;
  }, [schema]);

  const selectedOption = useMemo(() => {
    return options.find(it => it.value === formData?.inventoryTicker);
  }, [formData, options]);

  const minDate = useMemo(() => {
    const date = addDays(new Date(formData.checkIn), 1);
    return format(date, "yyyy-MM-dd");
  }, [formData.checkIn]);

  return (
    <StyledModificationPriceManagement>
      <>
        <label>
          <FormattedMessage {...messages.room} />
        </label>
        <StyledSelector isDisabled={disabled} options={options} value={selectedOption} onChange={handleSelectorChange} />
        <label>
          <FormattedMessage {...messages.quantity} />
        </label>
        <InputGroup
          type="number"
          value={quantity}
          field={{ fieldName: "quantity" }}
          onChange={data => handleChangeQuantity(data)}
          disabled={false}
          readOnly
        />
      </>
      <>
        <label>
          <FormattedMessage {...messages.checkIn} />
        </label>
        <DatePickerGroupWrapper>
          <DatePickerGroup
            formValues={{ startDate: formData.checkIn, endDate: formData.checkOut }}
            hasErrors={rawErrors}
            onChange={data => {
              handleChangeDate(data, "checkIn");
            }}
            disabled={disabled}
            value={formData.checkIn}
            field={{
              fieldName: "startDate",
              selectsStart: true,
              isRange: true,
              startField: "startDate",
              endField: "endDate",
            }}
            isClearable={!disabled}
          />
          <label>
            {" "}
            <FormattedMessage {...messages.checkOut} />
          </label>
          <DatePickerGroup
            formValues={{ startDate: formData.checkIn, endDate: formData.checkOut }}
            hasErrors={rawErrors}
            onChange={data => {
              handleChangeDate(data, "checkOut");
            }}
            disabled={disabled}
            value={formData.checkOut}
            field={{
              fieldName: "endDate",
              selectsEnd: true,
              isRange: true,
              startField: "startDate",
              endField: "endDate",
            }}
            minDate={minDate}
            isClearable={!disabled}
          />
        </DatePickerGroupWrapper>
      </>
      <BookingDetails>
        <label>
          <FormattedMessage {...messages.comments} />
        </label>
        <TextAreaGroup
          value={comments}
          field={{ fieldName: "comments" }}
          onChange={data => handleChangeString("comments", data)}
          rows={10}
        />

        <label>
          <FormattedMessage {...messages.internalNotes} />
        </label>
        <TextAreaGroup
          value={internalNotes}
          field={{ fieldName: "internalNotes" }}
          onChange={data => handleChangeString("internalNotes", data)}
          rows={10}
        />
      </BookingDetails>
      {!disabled && (
        <>
          <h5>
            <FormattedMessage {...messages.netPrice} />
          </h5>
          <HelpTextMessageNoMargin>
            * <FormattedMessage {...messages.usePoint} />
          </HelpTextMessageNoMargin>
          <HelpTextMessage>
            * <FormattedMessage {...messages.noRate} />
          </HelpTextMessage>
          <Grid>
            <GridDate header>
              <FormattedMessage {...messages.dateHeader} />
            </GridDate>
            <GridAriPrice header>
              <FormattedMessage {...messages.ariPriceHeader} />
            </GridAriPrice>
            <GridAppliedPrice header>
              <FormattedMessage {...messages.appliedPriceHeader} />
            </GridAppliedPrice>
            <GridDiscount header>
              <FormattedMessage {...messages.discountHeader} />
              <StyledFontAwesomeIconWrapper
                data-tip="React-tooltip"
                data-for="discount"
                className={"styled-font-awesome-icon-wrapper"}>
                <FontAwesomeIcon icon={faInfo} size={"xs"} />
              </StyledFontAwesomeIconWrapper>
              <StyledReactTooltip id="discount" effect="solid" multiline={true}>
                <FormattedMessage {...messages.discountTooltip} />
              </StyledReactTooltip>
            </GridDiscount>
            <GridFinalPrice header>
              <FormattedMessage {...messages.finalPriceHeader} />
            </GridFinalPrice>
            <GridSeparator />
            <GridRestrictions header>
              <FormattedMessage {...messages.restrictionsHeader} />
            </GridRestrictions>
            <GridPromos header>
              <FormattedMessage {...messages.promosHeader} />
            </GridPromos>
            {rows &&
              rows.map((row, index) => (
                <GridRow
                  row={row}
                  index={index}
                  rows={rows}
                  formData={formData}
                  handleChange={handleChange}
                  intl={intl}
                  finalPrices={finalPrices}
                  handleDeletePrice={handleDeletePrice}
                  selectedOption={selectedOption}
                  defaultCurrency={info.defaultCurrency}
                  regex={regex}
                />
              ))}
          </Grid>
          <NetPriceTotalWrapper>
            <h6>
              <FormattedMessage {...messages.netPriceTotal} />: <span>{valuesTotal}</span>
            </h6>
            <div>
              <span>
                * <FormattedMessage {...messages.netPriceTotalHelpText} />
              </span>
            </div>
          </NetPriceTotalWrapper>
        </>
      )}

      {!!extras?.length && (
        <>
          <h5>
            <FormattedMessage {...messages.extras} />
          </h5>
          <Extras>
            {extras.map((extra: DailyValues, index: number) => (
              <PriceLine>
                <span>
                  {/*@ts-ignore*/}
                  <span>{extra.name} </span>
                  {/*@ts-ignore*/}
                  <span>({extra.ticker})</span>:
                </span>

                <InputGroup
                  type="number"
                  value={extra.amount}
                  field={{ fieldName: extra.ticker }}
                  disabled={true}
                  readOnly={true}
                />
                {!disabled && (
                  <CloseIcon onClick={() => handleDeleteExtras(index)}>
                    <em className="fa fa-lg fa-times" />
                  </CloseIcon>
                )}
              </PriceLine>
            ))}
          </Extras>
          <ExtrasTotal>
            <h6>
              <FormattedMessage {...messages.extrasTotal} />: <span>{extrasTotal}</span>
            </h6>
          </ExtrasTotal>
        </>
      )}
      <GrandTotalWrapper>
        <FormattedMessage {...messages.grandTotal} />: {grandTotal}
      </GrandTotalWrapper>
      <PriceLine>
        <span>
          {" "}
          <FormattedMessage {...messages.deposit} />:
        </span>
        <InputGroup
          type="text"
          value={guaranteeAmount}
          field={{ fieldName: "guaranteeAmount" }}
          onChange={data => handleChangeDeposit(data)}
          disabled={false}
          readOnly={false}
        />
      </PriceLine>
    </StyledModificationPriceManagement>
  );
}
